'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { usePathname } from 'next/navigation';
import dynamic from 'next/dynamic';
import type { VoiceNavAction, VoiceJobListRow } from '@/lib/voice/transcript-storage';
import type { ChoiceSet, MutationRecord } from '@/lib/engine/tools';
import type { StagedAction } from '@/lib/engine/stage-action';
import { BAR_VOICE_START_EVENT } from '@/lib/chat/bar-bridge';
import { trackMicrophoneRelease, waitForMicrophoneRelease } from '@/lib/voice/microphone-release';
import { primeRinging, ringInPage, ringOff, ringOn } from '@/lib/voice/ringing';
import { selectNativeChat } from '@/lib/native/voice-chat';
import { deliverVoiceMessage, type VoiceMessageDelivery, type VoiceMessageSender } from '@/lib/voice/message-delivery';
import { pingWhileTalkingToMucka } from '@/lib/native/mucka-location';

// The SDK-heavy half (ElevenLabs WebRTC) loads only when voice is first used —
// it's not in the shared authed bundle. This provider stays lightweight: it
// owns the React state/context every page reads, and bridges to the lazily
// mounted engine via a controller (setters down) + handle (controls up).
const loadVoiceEngine = () => import('./VoiceEngine');
export function preloadVoiceEngine(): void { void loadVoiceEngine().catch(() => {}); }
const VoiceEngine = dynamic(loadVoiceEngine, { ssr: false });
const NativeVoiceEngine = dynamic(() => import('./NativeVoiceEngine'), { ssr: false });

/**
 * Start fetching the voice engine before anybody asks for it.
 *
 * 🚨 `VoiceEngine` pulls in the ElevenLabs SDK and is deliberately dynamic, so
 * the chunk does not start downloading until a session is wanted. For the mic
 * button that is right: the tap is the first anyone knows, and there is a
 * spinner on screen while it lands.
 *
 * The wake word knows earlier. It is already listening for its name, so the
 * download can be finished and waiting by the time it hears one — and there it
 * matters more, because the user is stood there having said something out loud
 * with nothing on screen yet. Measured at ~6s from "Alright Mucka" to connected
 * on a phone, most of it here.
 *
 * Safe to call repeatedly: the module cache makes every call after the first a
 * no-op, and it mounts nothing, takes no microphone and opens no session.
 */

export type VoiceState = 'idle' | 'connecting' | 'listening' | 'transcribing' | 'thinking' | 'speaking';

export interface VoiceTranscriptMessage {
  role: 'user' | 'mucka';
  text: string;
  /** Own-brain UI identity, independent of provider speech text. Never a bearer session ID. */
  turnId?: string;
  eventSeq?: number;
  userText?: string;
  navActions?: VoiceNavAction[];
  /** Rows for an on-screen job list, built server-side so every row reads the
   *  same way and none can be invented. The spoken summary still carries the
   *  answer; this is what someone looking at the screen can scan and tap. */
  jobList?: VoiceJobListRow[];
  /** Tap-to-answer chips the agent offered via `ask_user`, hung off this reply
   *  the same way navActions are. The question is still SPOKEN — these are a
   *  shortcut for whoever is looking at the screen, never a replacement. */
  choices?: ChoiceSet;
  helpArticles?: string[];
  skillAction?: import('@/lib/chat/skill-action').SkillInstallAction;
  /** What the own-brain loop changed this turn, as the tick-pills the typed
   *  rail draws. Delivered over the brain's side channel, never by ElevenLabs. */
  mutations?: MutationRecord[];
  /** Approval cards staged this turn (send this email, record that payment).
   *  Same source as `mutations`. */
  stagedActions?: StagedAction[];
  /** Server-built cards a voice tool produced (ui-blocks.ts) — the supplier
   *  order basket, a job brief. Delivered beside the reply, never by
   *  ElevenLabs: see src/lib/voice/voice-blocks.ts. */
  blocks?: import('@/lib/chat/ui-blocks').UiBlock[];
  /** This message carries ONLY extras (chips, buttons, a list, cards), for a
   *  reply that is already on screen. `ask_user` is called AFTER the question
   *  has been asked out loud on 84 of 95 production calls, so there is usually
   *  no "next reply" to hang them off — see src/lib/voice/attach-choices.ts.
   *  The brain's side channel has the same either-side ordering. The handler
   *  returns true when it found a reply to put them on; false means keep
   *  buffering. */
  attachOnly?: boolean;
  /** Tentative agent text streamed mid-generation (ElevenLabs
   *  `tentative_agent_response`). Cumulative full text, not a delta; may be
   *  revised. The final non-partial message for the turn is authoritative and
   *  replaces whatever streamed. */
  partial?: boolean;
  /** Which utterance of the caller's this message belongs to (own-brain only).
   *  A re-transcription arrives as a NEW turn with the SAME ordinal, so this is
   *  what lets the panel rewrite the bubble instead of adding another — see
   *  `upsertTurnMessage`. Absent on the provider agent, where nothing changes. */
  ordinal?: number;
  /** The session's opening line, spoken before the caller has said anything.
   *  It streams exactly like a reply does, so nothing in the text distinguishes
   *  it — and a user turn must never be placed above it. See the placement rule
   *  in MuckaPanel's transcript sink. */
  greeting?: boolean;
}

/**
 * Returns true for accepted turn-owned updates and attached legacy extras.
 * A missing sink returns false so the engine can retry without replaying tools.
 */
export type TranscriptHandler = (msg: VoiceTranscriptMessage) => boolean | void;

/**
 * Called when a session resolves which chat thread it belongs to. The panel
 * adopts the id so the next TYPED turn threads onto the same conversation —
 * without this, tapping the mic before typing anything would open a thread the
 * text rail never learns about, and the fork we just closed reopens.
 */
export type ConversationIdHandler = (conversationId: string) => void;

/**
 * Why a voice session failed to open, when we can tell. Lets the surfacing UI
 * (MuckaPanel) offer the right remedy — an Upgrade button for `upgrade_required`
 * rather than a raw "Voice session failed (402)" string. `null` means we only
 * have a message, not a recognised cause.
 */
export type VoiceErrorCode =
  | 'upgrade_required'
  | 'voice_budget_exceeded'
  | 'rate_limited'
  | 'mic_denied'
  /**
   * The one-and-done setup chat has already been had. NOT a fault: nothing is
   * broken and nothing needs retrying, so anything reading these codes must not
   * offer "try again" for it. Everything the chat covers is on the setup guide.
   */
  | 'setup_chat_already_used';

// Setters the engine pushes live-session state up through.
export interface VoiceEngineController {
  setConnecting: (v: boolean) => void;
  setActive: (v: boolean) => void;
  /**
   * `fromServer` says the message was written by `/api/elevenlabs/session` for
   * a person to read, rather than being an SDK throw's `err.message`. The chat
   * shows the first verbatim and hides the second behind a generic line — see
   * `ClassifiedVoiceError.fromServer`.
   */
  setError: (message: string | null, code?: VoiceErrorCode | null, fromServer?: boolean) => void;
  setIsSpeaking: (v: boolean) => void;
  /** Mucka has the turn and is working on it — the panel's three dots. */
  setThinking: (v: boolean) => void;
  /** A tool is running on the call: the progress line to show, or null
   *  (lib/voice/voice-blocks.ts). */
  setWorking?: (line: string | null) => void;
  /**
   * The line dropped and the call is holding on for it (TKT-1462). Only the
   * native runtime can tell; the browser engine never calls this.
   */
  setReconnecting?: (v: boolean) => void;
  emitTranscript: (msg: VoiceTranscriptMessage) => boolean;
  getPage: () => string;
  /** The chat thread this session should join, if the panel has one yet. */
  getConversationId: () => string | null;
  /** The thread the session actually resolved to — may be freshly minted. */
  emitConversationId: (conversationId: string) => void;
  /**
   * Which agent this session is for, read at START time.
   *
   * A getter rather than an argument for the same reason `getPage` is one: the
   * FIRST call of a page load has no engine mounted yet, so `startSession`
   * only sets a flag and the engine auto-starts itself once its chunk has
   * loaded. An argument passed to `startSession` would be gone by then.
   */
  getSessionMode: () => VoiceSessionMode;
}

// Imperative controls the engine hands back once it's mounted.
/**
 * Which conversation a session opens.
 *
 * `null` is the ordinary in-app assistant. `'setup'` is the one-and-done "Set up
 * with Mucka" chat — a DIFFERENT ElevenLabs agent, with its own prompt and its
 * own eight client tools, deliberately run through this same chrome so it looks
 * and behaves like every other conversation in the product.
 */
export type VoiceSessionMode = null | 'setup';

export interface VoiceEngineHandle {
  startSession: () => Promise<void>;
  endSession: () => Promise<void> | void;
  sendContextualUpdate: (text: string, context?: import('@/lib/voice/brain-context').BrainContextUpdate) => void;
  /** Acknowledges delivery; only not_sent permits text-engine fallback. */
  sendUserMessage: VoiceMessageSender;
  /**
   * Something the user DID on screen (a card tap), given to the agent as a
   * user turn and not echoed into the chat. Optional: an engine without one
   * gets it as a contextual update. See `chat/card-decision-update.ts`.
   */
  sendUserAction?: (text: string) => void;
}

interface VoiceCtxValue {
  /** Release the wake microphone before either manual or wake starts. */
  registerBeforeStart: (release: () => Promise<void>) => () => void;
  state: VoiceState;
  active: boolean;
  connecting: boolean;
  isSpeaking: boolean;
  /** Show the three dots: heard you, reply on its way. */
  thinking: boolean;
  /** A live call lost signal and is trying to pick itself back up. */
  reconnecting: boolean;
  /** What a tool on the call is doing right now ("Checking the diary…"), or null. */
  working: string | null;
  error: string | null;
  /** Recognised cause of `error`, when we could classify it. */
  errorCode: VoiceErrorCode | null;
  /** `error` is the route's own sentence, safe to show verbatim. */
  errorFromServer: boolean;
  page: string;
  /**
   * Open a call. `opts.mode` picks which agent — omit it for the ordinary
   * assistant. See VoiceSessionMode.
   */
  startSession: (opts?: { mode?: VoiceSessionMode }) => Promise<void>;
  endSession: () => void;
  sendContextualUpdate: (text: string, context?: import('@/lib/voice/brain-context').BrainContextUpdate) => void;
  /** Wait for delivery before deciding whether to use the text engine. */
  sendUserMessage: (text: string) => Promise<VoiceMessageDelivery>;
  /** What the user just did on screen, as a turn of theirs (`VoiceEngineHandle`). */
  sendUserAction: (text: string) => void;
  setOnTranscript: (handler: TranscriptHandler | null) => void;
  /** Tell voice which chat thread the panel is currently on. */
  setConversationId: (conversationId: string | null) => void;
  /** Hear back which thread a session resolved to (it may have minted one). */
  setOnConversationId: (handler: ConversationIdHandler | null) => void;
}

const VoiceCtx = createContext<VoiceCtxValue | null>(null);

export function useVoice(): VoiceCtxValue {
  const ctx = useContext(VoiceCtx);
  if (!ctx) {
    throw new Error('useVoice must be used inside <VoiceProvider>');
  }
  return ctx;
}

/**
 * The context or null, for a caller that must not bring a page down when it is
 * rendered outside the provider.
 *
 * In the app it never is — `AuthedShell` wraps every authed page in
 * `<VoiceProvider>`. This exists for the bare-component render: a parity test
 * mounts `JobDetailContent` on its own, and a thrown `useVoice` there fails
 * fourteen assertions about paperwork for a reason that has nothing to do with
 * paperwork. Same defensive shape, and same reasoning, as `useIsMobile`.
 */
export function useVoiceOptional(): VoiceCtxValue | null {
  return useContext(VoiceCtx);
}

export function pathnameToPage(pathname: string): string {
  if (!pathname || pathname === '/') return 'overview';
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length === 0) return 'overview';
  if (segments[0] === 'jobs' && segments.length > 1) return 'job-detail';
  return segments[0];
}

export default function VoiceProvider({ children }: { children: ReactNode }) {
  const pathname = usePathname();
  const page = pathnameToPage(pathname || '/');
  // Updated in a post-commit effect rather than assigned during render: a
  // render-phase ref write is unsound under concurrent rendering, because React
  // can discard a render but not the mutation it performed. Safe to defer here
  // because pageRef has exactly one consumer — `getPage()` on the controller
  // below, called from VoiceEngine when it builds the session-start fetch body.
  const pageRef = useRef(page);
  useEffect(() => {
    pageRef.current = page;
  });

  const [native, setNative] = useState(false);
  const [restoreOnly, setRestoreOnly] = useState(false);
  const [engaged, setEngaged] = useState<number | null>(null);
  const generationRef = useRef(0);
  const busyRef = useRef(false);
  const mountedRef = useRef(true);
  const beforeStartRef = useRef<(() => Promise<void>) | null>(null);
  useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; }; }, []);
  const registerBeforeStart = useCallback((release: () => Promise<void>) => {
    beforeStartRef.current = release;
    return () => { if (beforeStartRef.current === release) beforeStartRef.current = null; };
  }, []);
  const [active, setActive] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [error, setErrorMessage] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<VoiceErrorCode | null>(null);
  const [errorFromServer, setErrorFromServer] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [thinking, setThinking] = useState(false);
  const [working, setWorking] = useState<string | null>(null);
  const [reconnecting, setReconnecting] = useState(false);

  const setError = useCallback((message: string | null, code?: VoiceErrorCode | null, fromServer?: boolean) => {
    setErrorMessage(message);
    setErrorCode(message === null ? null : (code ?? null));
    // Defaults to false, so a caller that knows nothing about provenance can
    // never accidentally promote a raw throw into something we read out.
    setErrorFromServer(message === null ? false : fromServer === true);
  }, []);

  const onTranscriptRef = useRef<TranscriptHandler | null>(null);
  const handleRef = useRef<VoiceEngineHandle | null>(null);
  /** Which agent the NEXT session opens against. Cleared when a call ends. */
  const sessionModeRef = useRef<VoiceSessionMode>(null);
  const lastPageRef = useRef<string | null>(null);
  // Held in a ref, not state: its only consumer is `getConversationId()` on the
  // controller, read at session-start time. Same reasoning as pageRef above —
  // re-rendering the whole provider tree on every conversation id change would
  // be pure cost.
  const conversationIdRef = useRef<string | null>(null);
  const onConversationIdRef = useRef<ConversationIdHandler | null>(null);

  const state: VoiceState = useMemo(() => {
    if (connecting) return 'connecting';
    if (!active) return 'idle';
    if (isSpeaking) return 'speaking';
    return 'listening';
  }, [active, connecting, isSpeaking]);

  // Shared with the wake-word handoff on main: a second start cannot layer
  // another tone. No microphone, provider call or warm-up is started here.
  //
  // 🚨 This is the BACKSTOP for starting, not the trigger. An effect runs after
  // the commit that paints the spinner, so a ring hung off it can only ever
  // arrive after the spinner the user is looking at — and on a phone that is
  // also opening a sheet in the same frame, late enough to hear. `startSession`
  // rings in the tap itself; this stays for the path that sets `connecting`
  // some other way, and is a no-op when the tap already did it.
  //
  // The OFF half is the real work here: whatever ends a connect — connected,
  // failed, cancelled, unmounted — lands as `connecting` going false.
  useEffect(() => {
    if (connecting) ringOn(false, 'connecting');
    else ringOff();
  }, [connecting]);
  useEffect(() => ringOff, []);

  // Broadcast state changes via window event so components mounted outside
  // VoiceProvider (e.g. MobileNav in app/layout.tsx) can react.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(
      new CustomEvent('voice-state-change', { detail: { state, active, reconnecting } }),
    );
  }, [state, active, reconnecting]);

  const startSession = useCallback(async (opts?: { mode?: VoiceSessionMode }) => {
    // A fresh position for "when do I need to leave?" (On Time Tracker only).
    pingWhileTalkingToMucka();
    if (busyRef.current) return;
    busyRef.current = true;
    // Recorded BEFORE anything below, because the first-use path does not start
    // the session itself — it mounts the engine, which starts once its chunk has
    // loaded and reads this back off the controller.
    sessionModeRef.current = opts?.mode ?? null;
    const generation = ++generationRef.current;
    // 🚨 Prime AND ring here, synchronously, inside the tap. Both halves matter
    // and for different reasons: priming must be in the gesture or iOS hands
    // back a suspended context that plays silence, and RINGING must be in the
    // gesture or it trails the spinner by a paint. The ring is the app saying
    // "I heard you" — a beat behind the thing it is reassuring you about is the
    // one place it cannot be.
    primeRinging();
    ringOn(false, 'tap');
    setError(null);
    setConnecting(true);
    const owns = () => mountedRef.current && generationRef.current === generation;
    let microphoneReleased = false;
    try {
      // The wake capture releases actual tracks, including a late permission
      // promise, before the SDK can ask for the microphone. No capture: no wait.
      if (beforeStartRef.current) await beforeStartRef.current();
      let timer: ReturnType<typeof setTimeout> | undefined;
      try {
        await Promise.race([
          waitForMicrophoneRelease(),
          new Promise<never>((_, reject) => { timer = setTimeout(() => reject(new Error('Previous call is still closing')), 1500); }),
        ]);
      } finally { clearTimeout(timer); }
      if (!owns()) return;
      microphoneReleased = true;
      const useNative = await selectNativeChat(sessionModeRef.current !== null);
      if (useNative && sessionModeRef.current !== null) throw new Error('End the current voice call before starting setup.');
      if (!owns()) return;
      handleRef.current = null;
      if (!useNative) ringInPage();
      setNative(useNative); setRestoreOnly(false);
      setEngaged(generation);
    } catch (err) {
      if (!owns()) return;
      busyRef.current = false;
      setConnecting(false);
      setError(microphoneReleased && err instanceof Error ? err.message : 'The microphone is still being released. Please try again.');
    }
  }, [setError]);

  // A WebView can be recreated while the OS keeps audio alive. Validate the
  // current sign-in/workspace before exposing the existing native session.
  useEffect(() => {
    let live = true;
    const initialGeneration = generationRef.current;
    void selectNativeChat(true).then(async restore => {
      if (!restore || !live || busyRef.current || initialGeneration !== generationRef.current) return;
      if (beforeStartRef.current) await beforeStartRef.current();
      if (!live || busyRef.current || initialGeneration !== generationRef.current) return;
      busyRef.current = true; setNative(true); setRestoreOnly(true);
      setEngaged(++generationRef.current);
    }).catch(() => {});
    return () => { live = false; };
  }, []);

  const endSession = useCallback(() => {
    // Invalidate first: ending a pending lazy mount or fetch must be final.
    generationRef.current++;
    busyRef.current = false;
    const released = handleRef.current?.endSession();
    if (released) void trackMicrophoneRelease(released).catch(() => {});
    handleRef.current = null;
    setEngaged(null);
    setActive(false); setConnecting(false); setThinking(false); setIsSpeaking(false); setReconnecting(false);
    ringOff();
    // Back to the ordinary assistant. Leaving 'setup' here would open the NEXT
    // call — a mic tap on any page, minutes later — against the setup agent,
    // which has none of the tools that call needs and cannot be started twice.
    sessionModeRef.current = null;
  }, []);

  // The Mucka Bar's mic drives voice from the bottom nav, which lives outside
  // this provider's tree, so both directions arrive as window events.
  //
  // Start is deliberately NOT "mobile-voice-tap": AuthedShell listens for that
  // one and opens the full-screen chat panel, which is exactly what the docked
  // bar exists to avoid. Voice starts, the page stays.
  useEffect(() => {
    const handler = () => endSession();
    window.addEventListener('mobile-voice-end', handler);
    return () => window.removeEventListener('mobile-voice-end', handler);
  }, [endSession]);

  useEffect(() => {
    // The bar lives outside this provider, so a caller there asks for a mode
    // through the event detail. Anything unrecognised falls back to the
    // ordinary assistant rather than refusing — a mic tap must always work.
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ mode?: unknown }>).detail;
      const mode: VoiceSessionMode = detail?.mode === 'setup' ? 'setup' : null;
      void startSession({ mode });
    };
    window.addEventListener(BAR_VOICE_START_EVENT, handler);
    return () => window.removeEventListener(BAR_VOICE_START_EVENT, handler);
  }, [startSession]);

  const sendContextualUpdate = useCallback(
    (text: string, context?: import('@/lib/voice/brain-context').BrainContextUpdate) => {
      if (!active) return;
      handleRef.current?.sendContextualUpdate(text, context);
    },
    [active],
  );

  const sendUserAction = useCallback(
    (text: string) => {
      if (!active) return;
      const handle = handleRef.current;
      if (handle?.sendUserAction) handle.sendUserAction(text);
      else handle?.sendContextualUpdate(text);
    },
    [active],
  );

  /**
   * Send typed text into the live call as a user turn.
   *
   * Returns whether it went — the caller needs to know, because the fallback is
   * to post to the text engine instead, and doing both would ask two agents the
   * same question.
   */
  const sendUserMessage = useCallback(
    async (text: string): Promise<VoiceMessageDelivery> => {
      if (!active || !handleRef.current?.sendUserMessage) return 'not_sent';
      return deliverVoiceMessage(handleRef.current.sendUserMessage, text);
    },
    [active],
  );

  const setOnTranscript = useCallback((handler: TranscriptHandler | null) => {
    onTranscriptRef.current = handler;
  }, []);

  const setConversationId = useCallback((conversationId: string | null) => {
    conversationIdRef.current = conversationId;
  }, []);

  const setOnConversationId = useCallback((handler: ConversationIdHandler | null) => {
    onConversationIdRef.current = handler;
  }, []);

  // Push a contextual update on navigation while voice is active so the agent
  // knows the user moved without re-establishing the session.
  useEffect(() => {
    if (lastPageRef.current === page) return;
    const previous = lastPageRef.current;
    lastPageRef.current = page;
    if (active && previous) {
      sendContextualUpdate(`User navigated from ${previous} to ${page}`);
    }
  }, [page, active, sendContextualUpdate]);

  // Stable controller handed to the engine — setters + a page getter (read
  // live via ref so the engine always sees the current page at start time).
  const controller = useMemo<VoiceEngineController>(() => {
    const owns = () => mountedRef.current && engaged !== null && generationRef.current === engaged;
    return {
      setConnecting: v => { if (owns()) setConnecting(v); },
      setActive: v => { if (owns()) { setActive(v); if (!v) busyRef.current = false; } },
      setError: (message, code, fromServer) => { if (owns()) { setError(message, code, fromServer); if (message) busyRef.current = false; } },
      setIsSpeaking: v => { if (owns()) setIsSpeaking(v); },
      setThinking: v => { if (owns()) setThinking(v); },
      setWorking: line => { if (owns()) setWorking(line); },
      setReconnecting: v => { if (owns()) setReconnecting(v); },
      emitTranscript: msg => owns() && onTranscriptRef.current?.(msg) === true,
      getPage: () => pageRef.current,
      getConversationId: () => conversationIdRef.current,
      emitConversationId: id => {
        if (!owns()) return;
        conversationIdRef.current = id;
        onConversationIdRef.current?.(id);
      },
      getSessionMode: () => sessionModeRef.current,
    };
  }, [engaged, setError]);

  const onEngineReady = useCallback((handle: VoiceEngineHandle) => {
    if (engaged !== null && generationRef.current === engaged) handleRef.current = handle;
  }, [engaged]);

  const value = useMemo<VoiceCtxValue>(
    () => ({
      registerBeforeStart,
      state,
      active,
      connecting,
      isSpeaking,
      thinking,
      reconnecting,
      working: active ? working : null,
      error,
      errorCode,
      errorFromServer,
      page,
      startSession,
      endSession,
      sendContextualUpdate,
      sendUserMessage,
      sendUserAction,
      setOnTranscript,
      setConversationId,
      setOnConversationId,
    }),
    [registerBeforeStart, state, active, connecting, isSpeaking, thinking, reconnecting, working, error, errorCode, errorFromServer, page, startSession, endSession, sendContextualUpdate, sendUserMessage, sendUserAction, setOnTranscript, setConversationId, setOnConversationId],
  );

  return (
    <VoiceCtx.Provider value={value}>
      {children}
      {engaged !== null && (
        native ? <NativeVoiceEngine key={engaged} controller={controller} restoreOnly={restoreOnly} onReady={onEngineReady} />
          : <VoiceEngine key={engaged} controller={controller} autoStart onReady={onEngineReady} />
      )}
    </VoiceCtx.Provider>
  );
}
